/** Rótulos e formatação pt-BR reaproveitados pelos componentes de estado. */
import type { DocumentFormat, EnvelopeStatus, SignatureLevel, SignerStatus } from '@argink/shared';
import {
  DOCUMENT_FORMAT_LABEL as LEXICON_DOCUMENT_FORMAT_LABEL,
  ENVELOPE_STATUS_LABEL as LEXICON_ENVELOPE_STATUS_LABEL,
  MODALITY_DESCRIPTION,
  MODALITY_LABEL,
  PARTICIPANT_STATUS_LABEL_SIGNER,
} from '@argink/shared/surface-lexicon';

/**
 * Máscara de e-mail para exibição — mostra só os 2 primeiros caracteres do
 * usuário + o domínio inteiro, nunca o endereço completo. Usada no fluxo
 * Simples antes/durante o envio de OTP.
 */
export function maskEmail(email: string | null): string {
  if (!email) return 'E-mail não disponível';
  const [local = '', domain = ''] = email.split('@');
  if (!domain) return '***';
  const shown = local.slice(0, Math.min(2, local.length));
  return `${shown}${'*'.repeat(Math.max(3, local.length - shown.length))}@${domain}`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function isCertExpired(notAfter: string): boolean {
  const t = Date.parse(notAfter);
  return Number.isFinite(t) && t < Date.now();
}

const SAO_PAULO_TZ = 'America/Sao_Paulo';

/**
 * D-034 — data/hora de validade de certificado (`not_after`/`not_before`, sempre
 * ISO-8601 UTC no corpo de erro) formatada NO FUSO America/Sao_Paulo, sempre —
 * independente do fuso do navegador do signatário. A norma ICP-Brasil não é
 * interpretável por fuso local, então não usamos o padrão `Intl` implícito do
 * ambiente (que `formatDate` acima usa de propósito para o resto da UI).
 */
export function formatSaoPauloDateTime(iso: string): { date: string; time: string } {
  try {
    const d = new Date(iso);
    return {
      date: new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeZone: SAO_PAULO_TZ }).format(d),
      time: new Intl.DateTimeFormat('pt-BR', { timeStyle: 'short', timeZone: SAO_PAULO_TZ }).format(d),
    };
  } catch {
    return { date: iso, time: '' };
  }
}

/**
 * Copy EXATA mandatada para as duas modalidades do MVP (mandato §5, D-039) —
 * não reescrever livremente. `avancada` continua existindo como rótulo interno
 * de pipeline no enum do motor (compatibilidade), mas NUNCA é ofertada nem
 * anunciada como modalidade disponível nesta UI (IMVP-14). Se um dado legado
 * trouxer `avancada` como nível de um signatário, `modalityCopyFor` devolve um
 * aviso explícito de "fora do MVP" em vez de fingir que é uma terceira opção
 * equivalente às duas reais.
 */
export interface ModalityCopy {
  title: string;
  detail: string;
}

/**
 * Deriva do léxico compartilhado (§12 do M6-A). Antes as MESMAS strings estavam escritas aqui
 * e em `apps/web/src/lib/copy.ts`, sem nada comparando as duas cópias — coincidiam hoje e
 * divergiriam no dia em que alguém editasse um lado só, que é como o `ready` já divergiu entre
 * os dois apps antes da R-UX01. O recorte de DUAS modalidades continua sendo desta tela (o MVP
 * não oferece `avancada`); o que sai daqui é a autoria das PALAVRAS.
 */
export const MODALITY_COPY: Record<'simples' | 'qualificada', ModalityCopy> = {
  simples: {
    title: MODALITY_LABEL.simples,
    detail: MODALITY_DESCRIPTION.simples,
  },
  qualificada: {
    title: MODALITY_LABEL.qualificada,
    detail: MODALITY_DESCRIPTION.qualificada,
  },
};

/** Só usado defensivamente — nunca deve aparecer numa cerimônia criada pelo MVP
 * (o BFF/Zod já recusam `avancada` na criação, IMVP-14). Nome claro de que NÃO é
 * uma modalidade oferecida, para nunca ser confundido com as duas reais. */
export const NON_MVP_LEVEL_NOTICE: ModalityCopy = {
  title: 'Nível interno do motor',
  detail: 'Não é uma modalidade oferecida por este produto (fora do escopo do MVP).',
};

export function modalityCopyFor(level: SignatureLevel): ModalityCopy {
  return level === 'simples' || level === 'qualificada' ? MODALITY_COPY[level] : NON_MVP_LEVEL_NOTICE;
}

/**
 * R-UX01 §54 — vem do léxico compartilhado, não é mais definido aqui. Este mapa e o de
 * `apps/web/src/lib/copy.ts` já tinham divergido no `ready` ("Pronto para iniciar" aqui,
 * "Pronto para disparo" lá). Estado do ENVIO não tem perspectiva: é o mesmo nos dois apps.
 */
export const ENVELOPE_STATUS_LABEL: Record<EnvelopeStatus, string> =
  LEXICON_ENVELOPE_STATUS_LABEL;

/**
 * Perspectiva do SIGNATÁRIO — ele lê sobre SI MESMO ("Aguardando sua vez"), enquanto o dono
 * lê sobre outra pessoa ("Aguardando convite"). As duas versões são intencionais e moram
 * juntas no léxico compartilhado.
 */
export const SIGNER_STATUS_LABEL: Record<SignerStatus, string> = PARTICIPANT_STATUS_LABEL_SIGNER;

/**
 * Vem do léxico compartilhado (recuperação de M6-A, ver surface-lexicon.ts). `doc.format` é
 * valor de CONTRATO (`pades`/`cades`/`xades`) — nunca renderizar cru (`.toUpperCase()`), mesmo
 * em "Detalhes técnicos": o rótulo amigável descreve o ARQUIVO, que é o que o signatário abriu.
 */
export const DOCUMENT_FORMAT_LABEL: Record<DocumentFormat, string> = LEXICON_DOCUMENT_FORMAT_LABEL;

export function sourceLabel(source: 'p12' | 'mscapi'): string {
  return source === 'p12' ? 'A1 (arquivo .p12/.pfx)' : 'A3 (token/smartcard)';
}
